
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * Quote struct
 */
class Quote {
  /**
   * Initialize struct
   * @param {!number=} price
   * @param {!number=} volume
   * @constructor
   */
  constructor (price = 0.0, volume = 0.0) {
    this.Price = price
    this.Volume = volume
  }

  /**
   * Copy struct (shallow copy)
   * @this {!Quote}
   * @param {!Quote} other Other struct
   * @returns {!Quote} This struct
   */
  copy (other) {
    if (other.Price != null) {
      this.Price = other.Price
    } else {
      this.Price = null
    }
    if (other.Volume != null) {
      this.Volume = other.Volume
    } else {
      this.Volume = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!Quote}
   * @returns {!Quote} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new QuoteModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new QuoteModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!Quote}
   * @param {!Quote} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof Quote)) {
      throw new TypeError('Instance of Quote is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!Quote}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Price: ((this.Price != null) ? this.Price : null),
      Volume: ((this.Volume != null) ? this.Volume : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return Quote.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!Quote} other Object value
   * @returns {!Quote} Created struct
   */
  static fromObject (other) {
    return new Quote().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!Quote}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return Quote.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!Quote}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 29
  }
}

export { Quote };

/**
 * Fast Binary Encoding Quote field model
 */
class FieldModelQuote extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Price = new fbe.FieldModelDouble(buffer, 4 + 4)
    this.Volume = new fbe.FieldModelDouble(buffer, this.Price.fbeOffset + this.Price.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelQuote}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelQuote}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Price.fbeSize + this.Volume.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelQuote}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Price.fbeExtra + this.Volume.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelQuote}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelQuote.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelQuote}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 29
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelQuote}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelQuote}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Price.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Price.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Price.fbeSize

    if ((fbeCurrentSize + this.Volume.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Volume.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Volume.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelQuote}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelQuote}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelQuote}
   * @param {!Quote} fbeValue Default value, defaults is new Quote()
   * @returns {!Quote} Quote value
   */
  get (fbeValue = new Quote()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelQuote}
   * @param {!Quote} fbeValue Quote value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Price.fbeSize) <= fbeStructSize) {
      fbeValue.Price = this.Price.get()
    } else {
      fbeValue.Price = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Price.fbeSize

    if ((fbeCurrentSize + this.Volume.fbeSize) <= fbeStructSize) {
      fbeValue.Volume = this.Volume.get()
    } else {
      fbeValue.Volume = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Volume.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelQuote}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelQuote}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelQuote}
   * @param {!Quote} fbeValue Quote value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelQuote}
   * @param {Quote} fbeValue Quote value
   */
  setFields (fbeValue) {
    this.Price.set(fbeValue.Price)
    this.Volume.set(fbeValue.Volume)
  }
}

export { FieldModelQuote };

/**
 * Fast Binary Encoding Quote model
 */
class QuoteModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelQuote(this.buffer, 4)
  }

  /**
   * Get the Quote model
   * @this {!QuoteModel}
   * @returns {!FieldModelQuote} model Quote model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!QuoteModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!QuoteModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return QuoteModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!QuoteModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelQuote.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!QuoteModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!QuoteModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!QuoteModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!QuoteModel}
   * @param {!Quote} value Quote value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!QuoteModel}
   * @param {!Quote} value Quote value, defaults is new Quote()
   * @return {!object} Deserialized Quote value and its size
   */
  deserialize (value = new Quote()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new Quote(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new Quote(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!QuoteModel}
   * @param {!number} prev Previous Quote model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { QuoteModel };
